import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core'
import FileCopyOutlined from '@material-ui/icons/FileCopyOutlined'
import GetAppOutlined from '@material-ui/icons/GetAppOutlined'
import MenuBookOutlined from '@material-ui/icons/MenuBookOutlined'
import VpnKey from '@material-ui/icons/VpnKey'
import { ClosableDialog } from 'components/controls/dialogs'
import { Button } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import {
  createApiKey,
  deleteApiKey,
  getApiDocsSpec,
  getPostmanCollection,
  listApiKeys,
} from 'lib/api'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { ApiKey, CreatedApiKey } from 'shared'

export const ApiKeysPresenter = (): JSX.Element => {
  const { t } = useTranslation()
  const { user } = useUser()
  const { enqueueSnackbar } = useSnackbar()
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  const [newTitle, setNewTitle] = useState('')
  const [tierAccepted, setTierAccepted] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null)
  const [keyToDelete, setKeyToDelete] = useState<ApiKey | null>(null)

  const fetchApiKeys = async () => {
    try {
      setApiKeys(await listApiKeys())
    } catch (e) {
      enqueueSnackbar(t('account.apiKeys.loadError'), { variant: 'error' })
    }
  }

  useEffect(() => {
    fetchApiKeys()
  }, [])

  if (!user.featureFlags?.api) return <></>

  const openCreate = () => {
    setNewTitle('')
    setTierAccepted(false)
    setCreatedKey(null)
    setCreateOpen(true)
  }

  const closeCreate = () => {
    setCreateOpen(false)
    setNewTitle('')
    setCreatedKey(null)
    fetchApiKeys()
  }

  const handleCreate = async () => {
    if (!tierAccepted || !newTitle.trim() || creating || createdKey) return

    setCreating(true)
    try {
      setCreatedKey(await createApiKey(newTitle.trim()))
    } catch (e) {
      enqueueSnackbar(t('account.apiKeys.createError'), { variant: 'error' })
    } finally {
      setCreating(false)
    }
  }

  const handleCopy = async () => {
    if (!createdKey) return

    try {
      await navigator.clipboard.writeText(createdKey.secretKey)
      enqueueSnackbar(t('account.apiKeys.copied'), { variant: 'success' })
    } catch (e) {
      enqueueSnackbar(t('account.apiKeys.copyError'), { variant: 'error' })
    }
  }

  const downloadJson = async (
    fetchJson: () => Promise<Record<string, unknown>>,
    filename: string,
    errorKey: string
  ) => {
    try {
      const json = await fetchJson()
      const url = window.URL.createObjectURL(
        new Blob([JSON.stringify(json, null, 2)], { type: 'application/json' })
      )
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      enqueueSnackbar(t(errorKey), { variant: 'error' })
    }
  }

  const handleDownloadSpec = () =>
    downloadJson(
      () => getApiDocsSpec('v2'),
      'powertools-openapi-v2.json',
      'account.apiKeys.downloadSpecError'
    )

  const handleDownloadPostmanCollection = () =>
    downloadJson(
      () => getPostmanCollection('v2'),
      'powertools-postman-collection-v2.json',
      'account.apiKeys.downloadPostmanCollectionError'
    )

  const handleDelete = async () => {
    if (!keyToDelete) return

    try {
      await deleteApiKey(keyToDelete.id)
      enqueueSnackbar(t('account.apiKeys.deleted'), { variant: 'success' })
    } catch (e) {
      enqueueSnackbar(t('account.apiKeys.deleteError'), { variant: 'error' })
    } finally {
      setKeyToDelete(null)
      fetchApiKeys()
    }
  }

  return (
    <Box style={{ maxWidth: 900 }}>
      <Typography variant="h4" gutterBottom>
        {t('account.apiKeys.title')}
      </Typography>
      <Divider />

      <Box marginTop={2} marginBottom={3}>
        <Typography variant="body1">{t('account.apiKeys.description')}</Typography>
      </Box>

      <Box marginBottom={3} display="flex" flexWrap="wrap" gridGap={16}>
        <Button variant="contained" onClick={openCreate} data-testid="create-api-key">
          {t('account.apiKeys.createNew')}
        </Button>
        <Button
          variant="outlined"
          onClick={() => window.open('/api_docs/v2', '_blank', 'noopener')}
          startIcon={<MenuBookOutlined />}
          data-testid="api-docs-link"
        >
          {t('account.apiKeys.documentation')}
        </Button>
        <Button
          variant="outlined"
          onClick={handleDownloadSpec}
          startIcon={<GetAppOutlined />}
          data-testid="api-docs-spec-download"
        >
          {t('account.apiKeys.downloadSpec')}
        </Button>
        <Button
          variant="outlined"
          onClick={handleDownloadPostmanCollection}
          startIcon={<GetAppOutlined />}
          data-testid="api-docs-postman-collection-download"
        >
          {t('account.apiKeys.downloadPostmanCollection')}
        </Button>
      </Box>

      <Typography variant="h6" gutterBottom>
        {t('account.apiKeys.listTitle')}
      </Typography>

      {apiKeys.length === 0 ? (
        <Typography color="textSecondary">{t('account.apiKeys.empty')}</Typography>
      ) : (
        <Paper variant="outlined">
          {apiKeys.map((apiKey, index) => (
            <Box key={apiKey.id}>
              {index > 0 && <Divider />}
              <Box display="flex" alignItems="center" padding={2}>
                <VpnKey color="action" style={{ marginRight: 16 }} />
                <Box flexGrow={1}>
                  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                    {apiKey.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('account.apiKeys.addedOn', {
                      date: new Date(apiKey.createdAt).toLocaleDateString(),
                    })}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {apiKey.lastUsedAt
                      ? t('account.apiKeys.lastUsedOn', {
                          date: new Date(apiKey.lastUsedAt).toLocaleDateString(),
                        })
                      : t('account.apiKeys.neverUsed')}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  onClick={() => setKeyToDelete(apiKey)}
                  data-testid="delete-api-key"
                >
                  {t('account.apiKeys.delete')}
                </Button>
              </Box>
            </Box>
          ))}
        </Paper>
      )}

      <ClosableDialog open={createOpen} onClose={closeCreate} maxWidth="sm">
        <Box marginTop={2}>
          <h3>{t('account.apiKeys.createTitle')}</h3>
        </Box>

        <Box marginTop={2}>
          <TextField
            autoFocus
            fullWidth
            label={t('account.apiKeys.nameLabel')}
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            disabled={!!createdKey}
            inputProps={{ 'data-testid': 'api-key-title-input' }}
          />
        </Box>

        <Box
          marginTop={3}
          padding={1.5}
          border={1}
          borderColor={tierAccepted ? 'primary.main' : 'divider'}
          borderRadius={4}
          bgcolor="action.hover"
        >
          <FormControlLabel
            style={{ alignItems: 'flex-start', margin: 0 }}
            control={
              <Checkbox
                checked={tierAccepted}
                onChange={(e) => setTierAccepted(e.target.checked)}
                disabled={!!createdKey}
                color="primary"
                style={{ padding: 0, marginRight: 12 }}
                data-testid="api-key-tier-accept"
              />
            }
            label={
              <Typography variant="body2">
                <Trans i18nKey="account.apiKeys.tierWarningAccept" components={{ 1: <strong /> }} />
              </Typography>
            }
          />
        </Box>

        <Box marginTop={3}>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!tierAccepted || !newTitle.trim() || creating || !!createdKey}
            data-testid="submit-api-key"
          >
            {t('account.apiKeys.create')}
          </Button>
        </Box>

        {createdKey && (
          <Box marginTop={2}>
            <Box display="flex" alignItems="center">
              <Typography
                variant="body2"
                color="primary"
                style={{ wordBreak: 'break-all' }}
                data-testid="api-key-secret"
              >
                {createdKey.secretKey}
              </Typography>
              <IconButton
                size="small"
                onClick={handleCopy}
                aria-label={t('account.apiKeys.copy')}
                style={{ marginLeft: 8 }}
              >
                <FileCopyOutlined fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="body2" color="error">
              {t('account.apiKeys.showOnceWarning')}
            </Typography>
          </Box>
        )}

        <Box marginTop={3} style={{ float: 'right' }}>
          <Button variant="outlined" onClick={closeCreate} data-testid="close-create-api-key">
            {t('common.close')}
          </Button>
        </Box>
      </ClosableDialog>

      <ClosableDialog open={!!keyToDelete} onClose={() => setKeyToDelete(null)} maxWidth="sm">
        <Box marginTop={2}>
          <h3>{t('account.apiKeys.deleteTitle')}</h3>
        </Box>
        <Box marginTop={2}>
          <Typography>
            {t('account.apiKeys.deleteConfirm', { name: keyToDelete?.title })}
          </Typography>
        </Box>
        <Box marginTop={3} style={{ float: 'right' }}>
          <Button
            variant="outlined"
            onClick={() => setKeyToDelete(null)}
            style={{ marginRight: '15px' }}
          >
            {t('common.close')}
          </Button>
          <Button variant="contained" onClick={handleDelete} data-testid="confirm-delete-api-key">
            {t('account.apiKeys.delete')}
          </Button>
        </Box>
      </ClosableDialog>
    </Box>
  )
}
